import React, { useMemo } from 'react';
import { Box, Card, CardContent, Divider, Skeleton, Typography } from '@mui/material';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as ReTooltip,
} from 'recharts';
import { useTranslate } from 'react-admin';
import { formatInt, formatMoneyShort } from 'src/utils/format';
import { EmptyContent } from 'src/components/empty-content';

export type PerformancePoint = {
  date: string;
  users: number;
  volume: number;
  revenue: number;
};

const TOOLTIP_BG = 'rgba(0,0,0,0.61)';

function PerformanceTooltip({ active, payload, t }: any) {
  if (!active || !payload?.length) return null;

  const row = payload?.[0]?.payload as PerformancePoint | undefined;
  if (!row) return null;

  return (
    <div
      style={{
        background: TOOLTIP_BG,
        borderRadius: 12,
        padding: '10px 12px',
        color: '#fff',
        fontSize: 12,
        fontWeight: 400,
        minWidth: 160,
      }}
    >
      <div style={{ marginBottom: 6 }}>{row.date}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
        <span>{t('module.dashboardV2.performance.tooltip.users', { _: 'Users' })}</span>
        <span>{formatInt(row.users ?? 0)}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>{t('module.dashboardV2.performance.tooltip.volume', { _: 'Volume' })}</span>
        <span>{formatMoneyShort(row.volume ?? 0)}</span>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
        <span>{t('module.dashboardV2.performance.tooltip.revenue', { _: 'Revenue' })}</span>
        <span>{formatMoneyShort(row.revenue ?? 0)}</span>
      </div>
    </div>
  );
}

export function PerformanceCard({
  title,
  data,
  loading,
}: {
  title: string;
  data: PerformancePoint[];
  loading?: boolean;
}) {
  const t = useTranslate();

  const tickEvery = useMemo(() => {
    const n = data?.length ?? 0;
    if (n <= 7) return 0;
    if (n <= 14) return 1;
    if (n <= 31) return 3;
    return Math.ceil(n / 8);
  }, [data]);

  const hasData = (data?.length ?? 0) > 0;

  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardContent sx={{ p: 2 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
          {title}
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ height: 320 }}>
          {loading ? (
            <Skeleton variant="rounded" height={320} />
          ) : !hasData ? (
            <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <EmptyContent title={t('common.noData', { _: 'No data' })} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 10, right: 30, bottom: 10, left: 0 }}>
                <CartesianGrid vertical={false} stroke="#E5E7EB" strokeDasharray="3 3" />

                <XAxis dataKey="date" tick={{ fontSize: 12 }} interval={tickEvery} />

                <YAxis tick={{ fontSize: 12 }} tickFormatter={(v) => formatMoneyShort(Number(v ?? 0))} />

                <ReTooltip
                  content={(props) => <PerformanceTooltip {...props} t={t} />}
                  cursor={{ stroke: '#CBD5E1', strokeWidth: 1 }}
                />

                {/* Volume */}
                <Line type="monotone" dataKey="volume" stroke="#8979ff" strokeWidth={2} dot={false} />

                {/* Revenue */}
                <Line type="monotone" dataKey="revenue" stroke="#ff9389" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>
      </CardContent>
    </Card>
  );
}